const EUR = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EUR0 = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export const formatEUR = (v) => EUR.format(v ?? 0);
export const formatEUR0 = (v) => EUR0.format(v ?? 0);

const CCY_SYMBOL = { EUR: "€", USD: "$", GBP: "£", CHF: "CHF", JPY: "¥" };
// Formatta un importo nella valuta nativa (es. 19,60 $). Default EUR.
export const formatMoney = (v, ccy = "EUR") => {
  const code = (ccy || "EUR").toUpperCase();
  const sym = CCY_SYMBOL[code] || code;
  const n = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v ?? 0);
  return code === "EUR" ? `${n} ${sym}` : `${n} ${sym}`;
};

export const formatSigned = (v) => {
  const f = EUR.format(Math.abs(v ?? 0));
  return v >= 0 ? `+${f}` : `−${f}`;
};

const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

export const monthNameIT = (d = new Date()) => MONTHS_IT[d.getMonth()];

export const relativeDay = (isoStr) => {
  const d = new Date(isoStr);
  const today = new Date();
  const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const tt = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((tt - dd) / 86400000);
  if (diff === 0) return "Oggi";
  if (diff === 1) return "Ieri";
  return `${d.getDate()} ${MONTHS_IT[d.getMonth()]}`;
};

export const formatTime = (isoStr) => {
  const d = new Date(isoStr);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const timeAgo = (isoStr) => {
  const diff = (Date.now() - new Date(isoStr + (isoStr.endsWith("Z") ? "" : "Z")).getTime()) / 1000;
  if (diff < 90) return "ora";
  if (diff < 3600) return `${Math.round(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h fa`;
  return `${Math.round(diff / 86400)}g fa`;
};

export const CATEGORY_COLORS = {
  "Entrate Lavoro": "#00FF87",
  "Altre Entrate": "#7CFFC4",
  Ristorazione: "#FF8A4D",
  "Spesa Alimentare": "#FFD60A",
  Trasporti: "#4D9CFF",
  Abbonamenti: "#9D4EDD",
  "Casa e Affitto": "#FF7A9C",
  "Bollette e Utenze": "#00D4FF",
  Svago: "#FF5FA2",
  Shopping: "#D9A6FF",
  Salute: "#4DFFDF",
  Viaggi: "#66B8FF",
  "Tasse e Commissioni": "#C0C6D9",
  Investimenti: "#7C9CFF",
  Altro: "#8A93A6",
};

export const categoryColor = (cat) => CATEGORY_COLORS[cat] || "#8A93A6";

/* ---------- sistema accenti a 3 livelli ---------- */
export const TYPE_ACCENT = {
  ALERT: "#FF4D4D",
  ANOMALY: "#FFD60A",
  FORECAST: "#00D4FF",
  INCOME_EXPECTED: "#00FF87",
  SUGGESTION: "#9D4EDD",
  TAX: "#F5A623",
  SUBSCRIPTION: "#FF7AB6",
  INVESTMENT: "#2DD4BF",
  INVEST_TIP: "#5EEAD4",
  MONTHLY_REPORT: "#A78BFA",
  SIGNAL: "#34D399",
};

/* glifi tecnici al posto delle emoji */
export const TYPE_GLYPH = {
  ALERT: "\u25b2",          /* ▲ */
  ANOMALY: "\u25c8",        /* ◈ */
  FORECAST: "\u25c6",       /* ◆ */
  INCOME_EXPECTED: "\u2295",/* ⊕ */
  SUGGESTION: "\u2b21",     /* ⬡ */
  TAX: "\u00a7",            /* § */
  SUBSCRIPTION: "\u27f3",   /* ⟳ */
  INVESTMENT: "\u2197",     /* ↗ */
  INVEST_TIP: "\u25ce",     /* ◎ */
  MONTHLY_REPORT: "\u03a3", /* Σ */
  SIGNAL: "\u25c9",         /* ◉ */
};

export const TYPE_LABELS = {
  ALERT: "Alert",
  ANOMALY: "Anomalia",
  FORECAST: "Proiezione",
  INCOME_EXPECTED: "Entrata attesa",
  SUGGESTION: "Suggerimento",
  TAX: "Fisco",
  SUBSCRIPTION: "Abbonamenti",
  INVESTMENT: "Investimenti",
  INVEST_TIP: "Investi",
  MONTHLY_REPORT: "Report mensile",
  SIGNAL: "Segnali",
};

/* glow per card: solo bordo, niente shadow morbide */
export const typeGlow = (type) => {
  const map = {
    ALERT: "rgba(255,77,77,0.18)",
    ANOMALY: "rgba(255,214,10,0.16)",
    FORECAST: "rgba(0,212,255,0.14)",
    INCOME_EXPECTED: "rgba(0,255,135,0.18)",
    SUGGESTION: "rgba(157,78,221,0.16)",
    TAX: "rgba(245,166,35,0.16)",
    SUBSCRIPTION: "rgba(255,122,182,0.16)",
    INVESTMENT: "rgba(45,212,191,0.16)",
    INVEST_TIP: "rgba(94,234,212,0.18)",
    MONTHLY_REPORT: "rgba(167,139,250,0.18)",
    SIGNAL: "rgba(52,211,153,0.18)",
  };
  return `0 0 0 1px ${map[type] || "rgba(255,255,255,0.06)"}`;
};

export const SEVERITY_COLORS = {
  danger: "#FF4D4D",
  info: "#4D9CFF",
  positive: "#00FF87",
  neutral: "rgba(255,255,255,0.92)",
};

export const prettyFactLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/^./, (c) => c.toUpperCase());

export const prettyFactValue = (key, value) => {
  if (typeof value === "number") {
    if (/_pct$|tasso|peso_|esposizione|soglia/i.test(key)) return `${value}%`;
    if (/saldo|spesa|importo|proiezione|differenza|media|costo|liquidita|budget|fisse|ritmo|risparmiat|entrate|uscite|piano|pac|investibile|delta/i.test(key)) {
      return formatEUR(value);
    }
    return String(value);
  }
  if (Array.isArray(value)) return value.join(" · ");
  return String(value);
};
